import React, { useEffect, useState } from "react";
import { Grid, TrendingUp } from "lucide-react";
import { api } from "../../api";
import { STATUS_LABELS, STATUS_DOTS } from "./constants";

export default function AnalyticsView({ tasksCount }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tasks/analytics/summary");
        setData(data);
      } catch { setData({ total: 0, completed: 0, remaining: 0, completion_rate: 0, breakdown: [] }); }
    })();
  }, [tasksCount]);

  if (!data) return <div className="p-8 text-slate-400 text-sm">Loading analytics…</div>;

  return (
    <div className="w-full h-full overflow-y-auto bg-gradient-to-br from-slate-50 to-slate-100 p-6" data-testid="tp-analytics">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="bg-white rounded-2xl shadow-md border border-slate-200">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2 text-slate-700 font-semibold">
              <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center"><Grid size={14} /></div>
              <span className="tracking-wider uppercase text-xs">Task Analytics</span>
            </div>
            <div className="text-xs font-bold bg-indigo-600 text-white px-3 py-1 rounded-full">{data.total} Total Tasks</div>
          </div>

          <div className="p-6">
            <div className="text-[11px] uppercase tracking-widest text-slate-500 flex items-center gap-1"><TrendingUp size={11} /> Overall Progress</div>
            <div className="mt-3 flex items-baseline justify-between">
              <div className="text-slate-600 text-sm font-semibold">Completion Rate</div>
              <div className="text-3xl font-extrabold text-indigo-600" data-testid="tp-analytics-rate">{data.completion_rate}%</div>
            </div>
            <div className="mt-3 h-3 rounded-full bg-slate-200 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all"
                style={{ width: `${data.completion_rate}%` }}
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
              <span>{data.completed} completed</span>
              <span>{data.remaining} remaining</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-md border border-slate-200">
          <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100 text-slate-700 font-semibold">
            <div className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center"><Grid size={14} /></div>
            <span className="tracking-wider uppercase text-xs">Status Breakdown</span>
          </div>
          <div className="p-5 space-y-3">
            {data.breakdown.length === 0 && <div className="text-xs text-slate-400">No tasks yet — create your first one to see the breakdown.</div>}
            {data.breakdown.map((b) => (
              <div key={b.status} className="flex items-center justify-between text-sm" data-testid={`tp-breakdown-${b.status}`}>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: STATUS_DOTS[b.status] || "#94a3b8" }} />
                  <span className="text-slate-700">{STATUS_LABELS[b.status] || b.status}</span>
                </div>
                <div className="text-slate-600 font-semibold">
                  {b.count} <span className="text-slate-400 font-normal">({b.percent}%)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
